import _ from 'lodash';
import { useSelector } from 'react-redux';

import { RenewalStatus } from '@appTypes/enums';
import type { PersonRegistrationData } from '@appTypes/esb';
import { useGetCompletePersonProfileQuery } from '@modules/rtk/esb-api';
import { organizationGuidSel } from '@shared/duck/selectors/general.selectors';

/**
 * Picks the registration to display: the one that expires last, restricted to
 * `forOrganization` when given. A person can hold several registrations in the
 * same org, so a plain `find` returns an arbitrary one.
 */
export function getCurrentRegistration(
  registrations: PersonRegistrationData[],
  /** only include registrations in the given org */
  forOrganization?: string,
) {
  const found = _.chain(registrations)
    .filter(item => {
      if (forOrganization) return item.organizationGuid === forOrganization;
      return true;
    })
    .orderBy(
      ['registrationExpireDt', 'registrationEffectiveDt'],
      ['desc', 'desc'],
    )
    .first()
    .value();

  return found;
}

function usePersonActiveRegistration(params: { personGuid: string }) {
  const organizationGuid = useSelector(organizationGuidSel);
  const { data, isFetching, isLoading } = useGetCompletePersonProfileQuery(
    {
      personGuid: params.personGuid,
    },
    { skip: !params.personGuid },
  );

  const activeRegistration = getCurrentRegistration(
    data?.registrarInfo || [],
    organizationGuid,
  );

  // const testRegData = [
  //   {
  //     organizationGuid: 'EEB2E0FD-AFB2-4E19-97EC-4CC9D5B8BF92',
  //     organizationName: 'Troop 0273',
  //     isAdvancePayRecharter: false,
  //     registrationId: 169579489,
  //     personPositionId: 169975279,
  //     registrationStatusId: 1756,
  //     registrationStatus: 'Re-Registered',
  //     registrationEffectiveDt: '2026-01-01',
  //     registrationExpireDt: '2026-02-28',
  //     isYearlyMembership: true,
  //     isYearlyMembershipRenewalPaid: false,
  //     isManuallyEnded: false,
  //     isAutoRenewalOptedOut: false,
  //     renewalOptOutReasonsId: null,
  //     renewalOptOutReasonsName: '',
  //     renewalStatus: 'Expired',
  //     isUnitLess: false,
  //     bsaOrderId: null,
  //   },
  //   {
  //     organizationGuid: 'EEB2E0FD-AFB2-4E19-97EC-4CC9D5B8BF92',
  //     organizationName: 'Troop 0273',
  //     isAdvancePayRecharter: false,
  //     registrationId: 169579489,
  //     personPositionId: 170225178,
  //     registrationStatusId: 1756,
  //     registrationStatus: 'Re-Registered',
  //     registrationEffectiveDt: '2026-01-01',
  //     registrationExpireDt: '2026-02-28',
  //     isYearlyMembership: true,
  //     isYearlyMembershipRenewalPaid: false,
  //     isManuallyEnded: false,
  //     isAutoRenewalOptedOut: false,
  //     renewalOptOutReasonsId: null,
  //     renewalOptOutReasonsName: '',
  //     renewalStatus: 'Expired',
  //     isUnitLess: false,
  //     bsaOrderId: null,
  //   },
  //   {
  //     organizationGuid: '489E3726-039E-446B-8B63-546C6EC5B4A7',
  //     organizationName: 'Pioneer 06',
  //     isAdvancePayRecharter: false,
  //     registrationId: 169303955,
  //     personPositionId: 170173385,
  //     registrationStatusId: 1770,
  //     registrationStatus: 'Multiple',
  //     registrationEffectiveDt: '2026-01-01',
  //     registrationExpireDt: '2026-12-31',
  //     isYearlyMembership: true,
  //     isYearlyMembershipRenewalPaid: false,
  //     isManuallyEnded: false,
  //     isAutoRenewalOptedOut: false,
  //     renewalOptOutReasonsId: null,
  //     renewalOptOutReasonsName: '',
  //     renewalStatus: 'Current',
  //     isUnitLess: false,
  //     bsaOrderId: null,
  //   },
  // ] as PersonRegistrationData[];

  // const testActiveRegistration = getCurrentRegistration(testRegData);

  // console.log('testActiveRegistration: ', testActiveRegistration);

  const renewalStatus = activeRegistration?.renewalStatus;
  const isOptedOut = renewalStatus === RenewalStatus.OptedOut;

  return {
    /** Raw response from API */
    data,
    isLoading,
    isFetching,
    activeRegistration,
    renewalStatus,
    isOptedOut,
  };
}

export default usePersonActiveRegistration;
